import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAgreementHistory, HISTORY_PER_PAGE } from '../services/agreementHistory';
import { agreementKeys } from './agreementKeys';

/** Fetched eagerly rather than on tab activation, so the tab opens without a spinner.
 *  It is its own query, so a slow or failing fetch never blanks the detail page. */
export function useAgreementHistory(id: string | undefined) {
    const [pageState, setPageState] = useState({ id, page: 1 });
    const requestedPage = pageState.id === id ? pageState.page : 1;
    const setPage = useCallback((next: number) => setPageState({ id, page: next }), [id]);

    const query = useQuery({
        queryKey: agreementKeys.historyPage(id ?? '', requestedPage, HISTORY_PER_PAGE),
        queryFn: () => fetchAgreementHistory(id!, { page: requestedPage, perPage: HISTORY_PER_PAGE }),
        enabled: !!id,
        placeholderData: (previousData, previousQuery) =>
            previousQuery?.queryKey[2] === (id ?? '') ? previousData : undefined,
    });

    /** Clamps the page STATE, not just the reported value, so the query refetches
     *  at the clamped page rather than showing an out-of-range page's empty body. */
    const lastPage = query.data?.meta.lastPage;

    useEffect(() => {
        if (lastPage !== undefined && requestedPage > lastPage) {
            setPageState({ id, page: lastPage });
        }
    }, [id, lastPage, requestedPage]);

    return { query, page: requestedPage, setPage };
}
